import React, { useState } from "react";
import { SubscriptionPlan } from "./models";

type BillingInterval = "yearly" | "monthly";

type PlanCardsProps = {
  plans: SubscriptionPlan[];
  currentPlanType: string | null;
  currentBillingInterval: BillingInterval | null;
  isFreeTrialSubscription: boolean;
  onSubscribe: (planType: string, billingInterval: BillingInterval) => void;
  onUpgrade: (planType: string, billingInterval: BillingInterval) => void;
  subscribeLoading: boolean;
  upgradeLoading: boolean;
};

const planOrder = ["basic", "premium", "pro"];
const planColors: Record<string, string> = {
  basic: "#48BB78",
  premium: "#FFBB28",
  pro: "#FF6028",
};

const planFeatures: Record<string, string[]> = {
  basic: [
    "1 sucursal",
    "2 usuarios",
    "500 ordenes/mes",
    "Soporte por email",
  ],
  premium: [
    "2 sucursales",
    "20 usuarios",
    "1,500 ordenes/mes",
    "Soporte prioritario",
    "Reportes avanzados",
  ],
  pro: [
    "4 sucursales",
    "Usuarios ilimitados",
    "5,000 ordenes/mes",
    "Soporte dedicado",
    "Reportes avanzados",
    "API access",
  ],
};

const savingsPercent: Record<string, number> = {
  basic: 37,
  premium: 29,
  pro: 31,
};

const PlanCards: React.FC<PlanCardsProps> = ({
  plans,
  currentPlanType,
  currentBillingInterval,
  isFreeTrialSubscription,
  onSubscribe,
  onUpgrade,
  subscribeLoading,
  upgradeLoading,
}) => {
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("yearly");
  const isMonthly = billingInterval === "monthly";

  const sortedPlans = [...plans].sort(
    (a, b) => planOrder.indexOf(a.key) - planOrder.indexOf(b.key),
  );

  const getButtonConfig = (planKey: string) => {
    if (!currentPlanType || currentPlanType === "free") {
      return {
        text: "Contratar",
        onClick: () => onSubscribe(planKey, billingInterval),
        disabled: subscribeLoading,
        variant: "btn-primary",
      };
    }

    const isSameInterval =
      !currentBillingInterval || currentBillingInterval === billingInterval;

    if (currentPlanType === planKey && isSameInterval) {
      if (isFreeTrialSubscription) {
        return {
          text: "Contratar Plan",
          onClick: () => onSubscribe(planKey, billingInterval),
          disabled: subscribeLoading,
          variant: "btn-primary",
        };
      }

      return {
        text: "Plan Actual",
        onClick: () => {},
        disabled: true,
        variant: "btn-secondary",
      };
    }

    if (isFreeTrialSubscription) {
      return {
        text: "Contratar Plan",
        onClick: () => onSubscribe(planKey, billingInterval),
        disabled: subscribeLoading,
        variant: "btn-primary",
      };
    }

    if (currentPlanType === planKey && !isSameInterval) {
      return {
        text: billingInterval === "yearly" ? "Cambiar a Anual" : "Cambiar a Mensual",
        onClick: () => onUpgrade(planKey, billingInterval),
        disabled: upgradeLoading,
        variant: "btn-warning",
      };
    }

    const currentIndex = planOrder.indexOf(currentPlanType);
    const targetIndex = planOrder.indexOf(planKey);

    if (targetIndex > currentIndex) {
      return {
        text: "Cambiar Plan",
        onClick: () => onUpgrade(planKey, billingInterval),
        disabled: upgradeLoading,
        variant: "btn-warning",
      };
    }

    return {
      text: "Contactar Soporte",
      onClick: () => {
        window.open("mailto:soporte@fooapp.mx?subject=Cambio de plan", "_blank");
      },
      disabled: false,
      variant: "btn-outline-secondary",
    };
  };

  return (
    <div>
      {/* Billing interval toggle */}
      <div className="text-center mb-4">
        <div
          className="d-inline-flex align-items-center"
          style={{
            background: "#fff",
            borderRadius: "50px",
            padding: "4px 6px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <button
            className="btn btn-sm px-4"
            style={{
              borderRadius: "50px",
              fontWeight: 600,
              background: isMonthly ? "#292929" : "transparent",
              color: isMonthly ? "#fff" : "#292929",
            }}
            onClick={() => setBillingInterval("monthly")}
          >
            Mensual
          </button>
          <button
            className="btn btn-sm px-4"
            style={{
              borderRadius: "50px",
              fontWeight: 600,
              background: !isMonthly ? "#292929" : "transparent",
              color: !isMonthly ? "#fff" : "#292929",
            }}
            onClick={() => setBillingInterval("yearly")}
          >
            Anual{" "}
            <span
              className="badge"
              style={{
                background: "#48BB78",
                color: "#fff",
                fontSize: "0.65rem",
                verticalAlign: "middle",
                marginLeft: "4px",
              }}
            >
              Ahorra hasta 37%
            </span>
          </button>
        </div>
      </div>

      {isFreeTrialSubscription && (
        <div
          className="p-3 mb-4 text-center"
          style={{
            backgroundColor: "#FFF9E6",
            borderRadius: "8px",
            border: "1px solid #FFBB2840",
          }}
        >
          <i className="fas fa-info-circle mr-2" style={{ color: "#FFBB28" }} />
          <span style={{ fontSize: "0.9rem" }}>
            Puedes contratar tu plan de pago ahora mismo, no es necesario
            esperar a que termine tu prueba gratuita.
          </span>
        </div>
      )}

      <div className="row">
        {sortedPlans.map((plan) => {
          const btn = getButtonConfig(plan.key);
          const isCurrentPlan =
            currentPlanType === plan.key &&
            (!currentBillingInterval || currentBillingInterval === billingInterval);
          const color = planColors[plan.key] || "#FFBB28";
          const features = planFeatures[plan.key] || [];
          const price = isMonthly ? plan.priceMonthly : plan.priceYearly;
          const period = isMonthly ? "/mes" : "/año";
          const savings = savingsPercent[plan.key] || 0;
          const monthlyEffective = isMonthly
            ? null
            : Math.round(plan.priceYearly / 12);

          return (
            <div className="col-md-4 mb-4" key={plan.key}>
              <div
                className="card h-100"
                style={{
                  borderTop: `4px solid ${color}`,
                  boxShadow: isCurrentPlan
                    ? `0 0 0 2px ${color}`
                    : "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div className="card-body d-flex flex-column">
                  {isCurrentPlan && (
                    <span
                      className="badge mb-2"
                      style={{
                        backgroundColor: color,
                        color: "#fff",
                        alignSelf: "flex-start",
                      }}
                    >
                      Plan Actual
                    </span>
                  )}
                  <h4 className="card-title">{plan.name}</h4>
                  <div className="mb-1">
                    <span style={{ fontSize: "2rem", fontWeight: "bold" }}>
                      ${price.toLocaleString("es-MX")}
                    </span>
                    <span className="text-muted"> {period} (incluye IVA)</span>
                  </div>
                  {!isMonthly && monthlyEffective && (
                    <p className="text-muted mb-3" style={{ fontSize: "0.85rem", marginTop: "-2px" }}>
                      ${monthlyEffective.toLocaleString("es-MX")}/mes efectivo
                      {savings > 0 && (
                        <span
                          className="badge ml-1"
                          style={{ background: "#48BB78", color: "#fff", fontSize: "0.7rem" }}
                        >
                          -{savings}%
                        </span>
                      )}
                    </p>
                  )}
                  {isMonthly && (
                    <p className="text-muted mb-3" style={{ fontSize: "0.85rem", marginTop: "-2px" }}>
                      ${plan.priceYearly.toLocaleString("es-MX")}/año si pagas anual
                    </p>
                  )}
                  <ul className="list-unstyled mb-4 flex-grow-1">
                    {features.map((feature, i) => (
                      <li key={i} className="mb-2">
                        <i
                          className="fas fa-check-circle mr-2"
                          style={{ color }}
                        />{" "}
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    className={`btn ${btn.variant} btn-block`}
                    onClick={btn.onClick}
                    disabled={btn.disabled}
                  >
                    {btn.text}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlanCards;
